// src/features/training/trainingShared.js
import React, { useState } from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { GraduationCap, Radio, BookOpen, Star, ChevronRight } from "lucide-react";

export const MODE_FILTERS = [
  { label: "All", value: "" },
  { label: "Classroom", value: "classroom" },
  { label: "Live Online", value: "liveonline" },
  { label: "Self-paced", value: "selfpaced" },
];

export const MODE_META = {
  classroom:  { icon: GraduationCap, gradient: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" },
  liveonline: { icon: Radio,         gradient: "linear-gradient(135deg, #FFF1F2, #FFE4E6)" },
  selfpaced:  { icon: BookOpen,      gradient: "linear-gradient(135deg, #F0FDF4, #DCFCE7)" },
  default:    { icon: GraduationCap, gradient: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" },
};

export function formatPrice(price, currency) {
  if (price == null) return null;
  const symbol = currency === "USD" ? "$" : "₹";
  return `${symbol}${Number(price).toLocaleString()}`;
}

export function TrainingThumb({ src, mode, isFeatured, height = 190 }) {
  const [failed, setFailed] = useState(!src);
  const meta = MODE_META[mode] ?? MODE_META.default;
  const Icon = meta.icon;

  return (
    <Box
      className="card-media card-media--cover"
      sx={{
        height,
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
        background: failed ? meta.gradient : "#F1F5FB",
        borderBottom: "1px solid rgba(15,23,42,0.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {!failed && src ? (
        <Box
          component="img"
          src={src}
          alt=""
          onError={() => setFailed(true)}
          className="training-thumb-img"
          sx={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s ease" }}
        />
      ) : (
        <Icon size={36} color="#1d4ed8" strokeWidth={1.5} />
      )}

      {isFeatured && (
        <Chip
          icon={<Star size={12} />}
          label="Featured"
          size="small"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: "#111827",
            color: "white",
            fontWeight: 600,
            fontSize: "0.65rem",
          }}
        />
      )}
    </Box>
  );
}

/**
 * Vertical list-style filter, styled after the "Categories" sidebar
 * pattern: a small caps label up top, then rows with a highlighted
 * left border + tinted background on the active row, chevron on the
 * right. Reused for the Level filter (and anything else list-shaped).
 *
 * `options` is built dynamically by the caller from whatever `level`
 * values actually appear in the fetched trainings — this component
 * itself has no opinion about what levels exist.
 */
export function FilterSidebar({ title, options, value, onChange }) {
  return (
    <Box sx={{ width: "100%" }}>
      {title && (
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: "#94A3B8",
            letterSpacing: "0.06em",
            mb: 1.5,
            pl: 1.5,
          }}
        >
          {title}
        </Typography>
      )}
      <Stack spacing={0.25}>
        {options.map((opt) => {
          const isActive = value === opt.value;
          return (
            <Box
              key={opt.value || "all"}
              role="button"
              tabIndex={0}
              onClick={() => onChange(opt.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") onChange(opt.value);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 1.1,
                borderRadius: "8px",
                cursor: "pointer",
                borderLeft: "3px solid",
                borderLeftColor: isActive ? "#2563EB" : "transparent",
                bgcolor: isActive ? "rgba(37,99,235,0.06)" : "transparent",
                transition: "background-color 0.15s ease, border-color 0.15s ease",
                "&:hover": {
                  bgcolor: isActive ? "rgba(37,99,235,0.08)" : "rgba(15,23,42,0.035)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? "#2563EB" : "#334155",
                }}
              >
                {opt.label}
              </Typography>
              <ChevronRight size={15} color={isActive ? "#2563EB" : "#CBD5E1"} />
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}